import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { MTGCard } from 'types/interfaces/mtgCard';
import {
  cardImageInputsFromCard,
  getCardImageUrl,
  makeCardImageErrorHandler,
} from 'utils/cardImageFallback';
import CardInfoCard from './CardInfoCard';
import CardActionMenu, { CardMenuOrigin } from './CardActionMenu';
import Points from 'components/Points';
import { decimalToPoints } from 'utils/decimalUtils';
import { CardFinish, FINISH_PRICE_KEY, getAvailableFinishes } from 'utils/finishes';
import { FinishTag } from 'components/FinishTag';
import { getPromoTreatments } from 'utils/promoTreatments';
import './ResultCard.css';

/** Below this rendered width the face drops its type line and signal rows.
 *  Measured, not derived from the size letter: at 1440px the grid renders
 *  S at 138.8px and M at 169.8px, and M grows past 190px when the filter
 *  panel is hidden — a letter-based rule would flip M's content on a
 *  filter toggle. */
const COMPACT_WIDTH = 150;

/**
 * What the tile actually reads off a card: the search-light row's shape. Named
 * so a second surface can hand it the same fields without pretending to be a
 * full MTGCard (Home's Recent Activity feed, Asana 1219036410957400). MTGCard
 * satisfies it, so Search is unchanged. Points are per finish, holderCount /
 * wantedCount are the two trade signals, scryfallId is the wire casing.
 */
export type ResultCardCard = Pick<
  MTGCard,
  | 'id'
  | 'name'
  | 'setCode'
  | 'setIconUrl'
  | 'collectorNumber'
  | 'finishes'
  | 'promoTypes'
  | 'holderCount'
  | 'wantedCount'
  | 'pointValue'
  | 'imageUris'
> &
  Partial<Pick<MTGCard, 'scryfallID' | 'setName' | 'typeLine' | 'printedName' | 'flavorName' | 'price' | 'cardFaces'>> & {
    scryfallId?: string;
    nonFoilPoints?: number | null;
    foilPoints?: number | null;
    etchedPoints?: number | null;
  };

interface ResultCardProps {
  card: ResultCardCard;
  isHovered: boolean;
  onHover: () => void;
  onLeave: () => void;
  isMobile: boolean;
  /** True when this card owns the single open action menu. */
  isMenuOpen: boolean;
  onOpenMenu: () => void;
  onCloseMenu: () => void;
  /**
   * Replaces the default metadata row under the art (points pill + "N
   * offering"). Home's feed puts the event's own points and its relative time
   * there; Search passes nothing and keeps the default.
   */
  meta?: React.ReactNode;
  /** The surface rendering the tile; threaded into the face's link state and the menu. */
  origin?: CardMenuOrigin;
  /**
   * Replaces the click's default (open the action menu). Search's Cards view
   * passes a navigation to the card's Versions page, since one tile there
   * stands for every printing (Asana 1219353862024235).
   */
  onActivate?: () => void;
}

/** Backend sends scryfallId; the type declares scryfallID. Accept either. */
const getScryfallId = (card: ResultCardCard): string => card.scryfallId ?? card.scryfallID ?? '';

/**
 * Points for one finish of a card, or null when the backend has no price for
 * it. Reads search-light's per-finish points (nonFoilPoints / foilPoints /
 * etchedPoints, backend Track B) first, then that finish's own price column.
 *
 * Never estimated and never coerced to 0 — an unpriced finish renders "???"
 * rather than claiming to be worthless.
 */
const getPoints = (card: ResultCardCard, finish: CardFinish): number | null => {
  const anyCard = card as unknown as {
    nonFoilPoints?: number | null;
    foilPoints?: number | null;
    etchedPoints?: number | null;
    pointValue?: number | null;
    price?: Array<Record<string, unknown>> | null;
  };

  const perFinish =
    finish === 'etched' ? anyCard.etchedPoints : finish === 'foil' ? anyCard.foilPoints : anyCard.nonFoilPoints;
  if (perFinish != null) return perFinish;
  if (finish === 'nonfoil' && anyCard.pointValue != null) return anyCard.pointValue;
  const raw = anyCard.price?.[0]?.[FINISH_PRICE_KEY[finish]];
  return raw != null ? decimalToPoints(raw) : null;
};

export const ResultCard: React.FC<ResultCardProps> = ({
  card,
  isHovered,
  onHover,
  onLeave,
  isMobile,
  isMenuOpen,
  onOpenMenu,
  onCloseMenu,
  meta,
  origin = 'search',
  onActivate,
}) => {
  // Step state lives in a ref, never a dataset attribute: React re-renders reset
  // dataset values and the handler would loop forever.
  const fallbackStepRef = useRef<Map<string | number, number>>(new Map());
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [isCompact, setIsCompact] = useState(false);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);

  // Measure the rendered card, not the size letter — see COMPACT_WIDTH.
  useLayoutEffect(() => {
    const node = cardRef.current;
    if (!node) return undefined;

    const apply = (width: number) => setIsCompact(width > 0 && width <= COMPACT_WIDTH);
    apply(node.getBoundingClientRect().width);

    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width;
      if (width !== undefined) apply(width);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // Keep the popover anchored while the page scrolls under it. The menu also
  // closes on scroll, so this only has to survive the frame before that fires.
  useEffect(() => {
    if (!isMenuOpen || isMobile) return;
    setAnchorRect(cardRef.current?.getBoundingClientRect() ?? null);
  }, [isMenuOpen, isMobile]);

  const scryfallId = getScryfallId(card);
  const imageInputs = cardImageInputsFromCard(card);
  const nonFoilPrice = getPoints(card, 'nonfoil');
  const foilPrice = getPoints(card, 'foil');
  const etchedPrice = getPoints(card, 'etched');
  // Finish chips only when the payload declares its finishes (backend Track B)
  // AND the set says something: a plain nonfoil+foil card (most of the
  // catalogue) gets no chip (QA round 1, 1.3). Chips appear when the set
  // includes etched, or when the printing carries a promo treatment; the tags
  // name the premium finishes, non-foil stays the unmarked default.
  const declaredFinishes: CardFinish[] | null = Array.isArray(card.finishes) ? getAvailableFinishes(card) : null;
  const treatment = getPromoTreatments(card.promoTypes)[0] ?? null;
  const finishChips =
    declaredFinishes && (declaredFinishes.includes('etched') || treatment)
      ? declaredFinishes.filter((f) => f !== 'nonfoil')
      : [];
  // A treatment is never its own chip: its swatch and muted name ride on the
  // premium finish tag (addendum 2, 5.3). A treated non-foil-only printing
  // (rare) tags non-foil so the treatment still shows.
  const chipFinishes: CardFinish[] = finishChips.length > 0 ? finishChips : treatment && declaredFinishes ? ['nonfoil'] : [];
  const treatedIndex = chipFinishes.length - 1;
  // holderCount IS the for-trade count — it shares one predicate with the
  // Available to receive filter, so the badge and the toggle cannot disagree.
  const offeredCount = card.holderCount;

  const signal = {
    offeredCount,
    wantedCount: card.wantedCount,
  };

  // Clicking opens the action menu; it deliberately no longer navigates.
  // Navigation moved to the face's title link and the menu's "Open card
  // details" row, which carries the Enter shortcut.
  const handleClick = () => {
    if (onActivate) {
      onActivate();
      return;
    }
    if (isMenuOpen) onCloseMenu();
    else onOpenMenu();
  };

  return (
    <div className="result-card-cell" data-testid="result-card">
      <div
        ref={cardRef}
        className="result-card"
        role="button"
        tabIndex={0}
        aria-haspopup="menu"
        aria-expanded={isMenuOpen}
        onMouseEnter={!isMobile ? onHover : undefined}
        onMouseLeave={!isMobile ? onLeave : undefined}
        onClick={handleClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick();
          }
        }}
      >
        {isHovered && !isMobile ? (
          <CardInfoCard
            title={card.name}
            altName={card.printedName || card.flavorName || undefined}
            cardId={scryfallId}
            setName={card.setName}
            setCode={card.setCode}
            setIconUrl={card.setIconUrl}
            collectorNumber={card.collectorNumber || undefined}
            typeLine={card.typeLine || undefined}
            nonFoilPrice={nonFoilPrice ?? undefined}
            foilPrice={foilPrice ?? undefined}
            etchedPrice={etchedPrice ?? undefined}
            finishes={declaredFinishes ?? undefined}
            signal={signal}
            compact={isCompact}
            onOpenActions={onOpenMenu}
            origin={origin}
          />
        ) : (
          <>
            <img
              className="result-card-image"
              src={getCardImageUrl(imageInputs, 'normal')}
              alt={card.name}
              loading="lazy"
              onError={makeCardImageErrorHandler(imageInputs, fallbackStepRef, scryfallId, 'normal')}
            />
            {chipFinishes.length > 0 && (
              <span className="result-card-finishes" aria-label="Finishes">
                {chipFinishes.map((f, i) => (
                  <FinishTag key={f} finish={f} treatment={i === treatedIndex ? treatment : null} />
                ))}
              </span>
            )}
          </>
        )}
      </div>

      {/* Outside the art box, which clips its own overflow. Points renders
          through the brand coin component; an unpriced card shows "???" rather
          than claiming to be worth nothing. */}
      <div className="result-card-meta">
        {meta !== undefined ? (
          meta
        ) : (
          <>
            {nonFoilPrice !== null ? (
              <Points type="small" points={nonFoilPrice} />
            ) : (
              <span className="result-card-unpriced">???</span>
            )}
            {/* "offering" counts people, not copies — holderCount is a
                COUNT(DISTINCT userId). */}
            {offeredCount !== undefined && (
              <span className="result-card-holders">{offeredCount} offering</span>
            )}
          </>
        )}
      </div>

      {isMenuOpen && (
        <CardActionMenu
          card={{
            name: card.name,
            id: card.id,
            scryfallId,
            setCode: card.setCode,
            collectorNumber: card.collectorNumber || undefined,
            points: nonFoilPrice,
          }}
          anchorRect={anchorRect}
          isMobile={isMobile}
          onClose={onCloseMenu}
          returnFocusTo={cardRef.current}
          offeredCount={offeredCount}
          wantedCount={card.wantedCount}
          origin={origin}
        />
      )}
    </div>
  );
};

export default ResultCard;
